import React, { useState, useEffect, useCallback } from "react";
import { setTitle } from "../helpers/MetaTag";
import { postData } from "../services/api";
import Button from "../components/form/Button";
import { useTranslation } from "react-i18next";
import toastr from 'toastr';
import { useSelector } from "react-redux";
import Input from "../components/form/Input";

const UserSettings = ({ masterOtpEnabled = false }) => {
  setTitle("Succession Planning Software for your Business with Succession Now | Settings");
  const { t } = useTranslation();
  const role = localStorage.getItem("role");
  const user = useSelector((state) => state.auth?.user);
  const [userInfo, setUserInfo] = useState({});
  const [profioleInfo, setProfileInfo] = useState({});
  const [emailOtpEnabled, setEmailOtpEnabled] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isManage, setIsManage] = useState(false);
  
  // API Key states
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [isEditingApiKey, setIsEditingApiKey] = useState(false);
  const [tempApiKey, setTempApiKey] = useState("");
  const [Loading,setLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      const userId = role === "customer-admin" ? user?._id : user?.customer_admin_id;
      const [siteResult, userResult, profileDetails] = await Promise.all([
        postData("setting/details", {}),
        postData("assign/details", { id: userId }),
        postData("profile/details", {})
      ]);
      
      if (siteResult?.data) {
        setIsDisabled(!siteResult.data.email_otp_enabled);
      }
      
      if (userResult?.status === 200) {
        setUserInfo(userResult.data);
      }
      if(profileDetails?.status === 200){
        setProfileInfo(profileDetails.data)
      }
    } catch (err) {
      console.error("Error fetching data:", err.message);
    }
  }, [role, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (Object.keys(userInfo).length > 0) {
      setEmailOtpEnabled(userInfo.email_otp_enabled);
      setApiKey(userInfo.api_key || "");
    }
    if (Object.keys(profioleInfo).length > 0) {
      setApiKey(profioleInfo?.api_key);
      setAutoSyncEnabled(profioleInfo?.auto_sync || false);
    }
  }, [userInfo,profioleInfo]);

  // Handle API Key edit mode
  const handleEditApiKey = () => {
    setTempApiKey(apiKey);
    setIsEditingApiKey(true);
  };

  // Cancel API Key edit
  const handleCancelApiKey = () => {
    setTempApiKey("");
    setIsEditingApiKey(false);
  };

  // Save API Key
  const handleSaveApiKey = async () => {
    setLoading(true);
    if (!tempApiKey.trim()) {
      setLoading(false);
      toastr.error("Please enter a valid API Key");
      return;
    }

    try {
      const result = await postData("user-settings/update-api-key", { 
        apiKey: tempApiKey.trim() 
      });
      
      if (result.status === 200) {
        setApiKey(tempApiKey.trim());
        setIsEditingApiKey(false);
        setTempApiKey("");
        toastr.success("API Key saved successfully!");
        fetchData();
      } else {
        toastr.error(result.message || "Failed to save API Key");
      }
      setLoading(false);
    } catch (err) {
      console.error("Error saving API Key:", err);
      toastr.error("Failed to save API Key");
      setLoading(false);
    }
  };

  // Copy API Key to clipboard
  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      toastr.success("API Key copied to clipboard!");
    }
  };

  // Delete API Key
  const handleDeleteApiKey = async () => {
    if (window.confirm("Are you sure you want to delete your API Key?")) {
      try {
        const result = await postData("user-settings/delete-api-key", {});
        if (result.status === 200) {
          setApiKey("");
          toastr.success("API Key deleted successfully!");
          fetchData();
        } else {
          toastr.error(result.message || "Failed to delete API Key");
        }
      } catch (err) {
        console.error("Error deleting API Key:", err);
        toastr.error("Failed to delete API Key");
      }
    }
  };

  // Manual Sync Data with API Key
  const handleManualSyncData = async () => {
    if (!apiKey) {
      toastr.error("Please configure an API Key first");
      return;
    }

    setIsSyncing(true);
    try {
      const result = await postData("user-settings/sync-data", {
        apiKey: apiKey
      });
      
      if (result.status === 200) {
        toastr.success(result.message || "Data synced successfully!");
        fetchData();
      } else {
        toastr.error(result.message || "Failed to sync data");
      }
    } catch (err) {
      console.error("Error syncing data:", err);
      toastr.error("Failed to sync data. Please try again.");
    } finally {
      setIsSyncing(false);
    }
  };

  // Toggle Auto Sync
  const handleToggleAutoSync = async () => {
    if (!apiKey) {
      toastr.error("Please configure an API Key first");
      return;
    }
    const newAutoSyncState = !autoSyncEnabled;    
    try {
      const result = await postData("user-settings/auto-sync", {
        auto_sync: newAutoSyncState
      });      
      if (result.status === 200) {
        setAutoSyncEnabled(newAutoSyncState);
        toastr.success(`Auto Sync ${newAutoSyncState ? 'enabled' : 'disabled'} successfully!`);
        fetchData();
      } else {
        toastr.error(result.message || "Failed to update auto sync setting");
      }
    } catch (err) {
      console.error("Error toggling auto sync:", err);
      toastr.error("Failed to update auto sync setting");
    }
  };

  const onSubmit2FA = async (e) => {
    e.preventDefault();
    setIsManage(true);    
    try {
      const result = await postData("user-settings/update", { emailOtpEnabled });
      if (result.status === 200) {
        toastr.success(result.message);
      } else {
        toastr.error(result.message);
      }
      setIsManage(false);
    } catch (err) {
      console.log(err.message);
      setIsManage(false);
    }
  };

  return (
    <div className="border bg-white border-gray-200 p-5 shadow-md rounded-xl">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">{t("user_settings")}</h2>
          <p className="text-sm text-gray-500 mt-1">Manage your account security and integration settings</p>
        </div>

        {masterOtpEnabled && (
          <button
            onClick={() => setIsDisabled(!isDisabled)}
            className={`px-4 py-1.5 text-sm rounded-lg border transition-all ${
              isDisabled
                ? "bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200"
                : "bg-teal-600 border-teal-700 text-white hover:bg-teal-700"
            }`}
          >
            {isDisabled ? t("enable_settings") : t("disable_settings")}
          </button>
        )}
      </div>

      {/* Section 1: Two-Factor Authentication */}
      {/* <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-1 bg-blue-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-800">Security Settings</h3>
        </div>

        <div className={`p-4 rounded-lg border transition-all ${
          isDisabled 
            ? "border-gray-200 bg-gray-50 opacity-60" 
            : "border-gray-200 bg-gray-50 hover:border-blue-200 hover:shadow-sm"
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-white rounded-md shadow-sm ${
                isDisabled ? "opacity-50" : ""
              }`}>
                <i className="fa-solid fa-shield-halved text-blue-600 text-xl"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">
                  Two-Factor Authentication (2FA)
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Enable extra security using email-based OTP verification
                </p>
                {isDisabled && (
                  <p className="text-xs text-red-500 mt-1">
                    <i className="fa-solid fa-lock mr-1"></i>
                    This setting is currently disabled
                  </p>
                )}
              </div>
            </div>
            
            <button
              disabled={isDisabled}
              onClick={() => setEmailOtpEnabled(!emailOtpEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                isDisabled
                  ? "bg-gray-300 cursor-not-allowed"
                  : emailOtpEnabled
                  ? "bg-blue-600"
                  : "bg-gray-300"
              }`}
            >
              <span
                className={`${
                  emailOtpEnabled ? "translate-x-6" : "translate-x-1"
                } inline-block h-4 w-4 transform rounded-full bg-white transition shadow-md`}
              />
            </button>
          </div>

          
          <div className="mt-4 pt-3 border-t border-gray-200 flex justify-end">
            <Button
              buttonClasses={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                (isDisabled || isManage) 
                  ? "!bg-gray-300 !text-gray-600 !cursor-not-allowed"
                  : "!bg-teal-600 !text-white !hover:bg-teal-700"
              }`}
              buttonType={"button"}
              buttonIcon={isManage ? "fa-light fa-spinner fa-spin" : "fa-light fa-save"}
              buttonIconPosition={"left"}
              buttonLabel={t("save_changes")}
              functions={onSubmit2FA}
              buttonHasLink={false}
              buttonDisabled={isDisabled || isManage}
            />
          </div>
        </div>
      </div> */}

      {/* Divider */}
      {/* <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t-2 border-gray-200"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wide">
            Integration Settings
          </span>
        </div>
      </div> */}

      {/* Section 2: API Key Management */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-1 bg-purple-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-800">API Configuration</h3>
        </div>

        <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 hover:border-purple-200 hover:shadow-sm transition-all">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-md shadow-sm">
                <i className="fa-solid fa-key text-purple-600 text-xl"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800">
                  API Key Management
                  <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                    Active
                  </span>
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Enter your third-party API key for integrations (e.g., HR performance pro.)
                </p>
              </div>
            </div>
            
            {/* Auto Sync Data Controls for API Key */}
            {apiKey && !isEditingApiKey && (
              <div className="flex items-center gap-3">
                {/* Auto Sync Toggle */}
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                  <span className="text-xs font-medium text-gray-700">Auto Sync</span>
                  <button
                    onClick={handleToggleAutoSync}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 shadow-inner ${
                      autoSyncEnabled
                        ? "bg-gradient-to-r from-green-500 to-emerald-500 shadow-green-200"
                        : "bg-gray-300"
                    }`}
                  >
                    <span className="sr-only">Toggle auto sync</span>
                    <span
                      className={`${
                        autoSyncEnabled ? "translate-x-6" : "translate-x-1"
                      } inline-flex h-4 w-4 items-center justify-center transform rounded-full bg-white transition-transform duration-300 shadow-md`}
                    >
                      {autoSyncEnabled && (
                        <i className="fa-solid fa-check text-green-600 text-xs"></i>
                      )}
                    </span>
                  </button>
                  {autoSyncEnabled && (
                    <span className="text-xs font-medium text-green-600 animate-pulse">
                      <i className="fa-solid fa-circle text-[6px] mr-1"></i>
                      Active
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* API Key Input Section */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            {isEditingApiKey ? (
              // Edit Mode
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                    Enter API Key
                  </label>
                  <div className="relative">
                    <Input
                      inputType={showApiKey ? "text" : "password"}
                      inputValue={tempApiKey}
                      setInput={setTempApiKey}
                      inputPlaceholder="123abxxxxxxxxxxxxxxxxxxxx"
                      inputClasses={"w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 "}
                    />
                    <Button
                      buttonType={'button'}
                      functions={() => setShowApiKey(!showApiKey)}
                      buttonClasses="!absolute !right-0 !top-1/2 -translate-y-1/2 !text-white !hover:text-gray-700"
                      buttonIcon={showApiKey ? "fa-light fa-eye-slash" : "fa-light fa-eye"}
                      buttonIconPosition={"left"}
                      buttonHasLink={false}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="fa-solid fa-info-circle mr-1"></i>
                    This key will be stored securely and used for third-party integrations
                  </p>
                </div>
                
                <div className="flex gap-2 justify-end pt-2">
                  <Button
                    buttonType={'button'}
                    functions={handleCancelApiKey}
                    buttonClasses={"!bg-gray-200 !text-gray-700 !py-2 !px-4 !rounded-lg !hover:bg-gray-300 !transition-colors !text-sm !font-medium"}
                    buttonIcon={"fa-light fa-times"}
                    buttonIconPosition={"left"}
                    buttonLabel={"Cancel"}
                  />
                  <Button
                    buttonType={'button'}
                    functions={handleSaveApiKey}
                    buttonClasses={"!bg-teal-600 !text-white !py-2 !px-4 !rounded-lg !hover:bg-teal-700 !transition-colors !text-sm !font-medium"}
                    buttonIcon={Loading ? "fa-light fa-spinner fa-spin" : "fa-light fa-check"}
                    buttonIconPosition={"left"}
                    buttonLabel={"Save API Key"}
                  />
                </div>
              </div>
            ) : apiKey ? (
              // Display Mode - Key exists
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-3 border border-gray-300 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-gray-600 uppercase">
                      Performance Pro API Key
                    </label>
                    <button
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="text-purple-600 hover:text-purple-700 text-sm transition-colors"
                    >
                      <i className={`fa-solid ${showApiKey ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  <code className="block text-sm font-mono text-gray-800 break-all bg-gray-50 p-2 rounded">
                    {showApiKey ? apiKey : '•'.repeat(Math.min(apiKey.length, 40))}
                  </code>
                </div>
                
                <div className="flex gap-2 flex-wrap justify-end pt-2">
                  <Button
                    buttonType={'button'}
                    functions={handleCopyApiKey}
                    buttonClasses={"!bg-blue-600 !text-white !py-2 !px-4 !rounded-lg !hover:bg-blue-700 !transition-colors !text-sm !font-medium"}
                    buttonIcon={"fa-solid fa-copy"}
                    buttonIconPosition={"left"}
                    buttonLabel={"Copy Key"}
                  />
                  <Button
                    buttonType={'button'}
                    functions={handleEditApiKey}
                    buttonClasses={"!bg-gray-600 !text-white !py-2 !px-4 !rounded-lg !hover:bg-gray-700 !transition-colors !text-sm !font-medium"}
                    buttonIcon={"fa-solid fa-edit"}
                    buttonIconPosition={"left"}
                    buttonLabel={"Edit Key"}
                  />
                  <Button
                    buttonType={'button'}
                    functions={handleDeleteApiKey}
                    buttonClasses={"!bg-red-600 !text-white !py-2 !px-4 !rounded-lg !hover:bg-red-700 !transition-colors !text-sm !font-medium"}
                    buttonIcon={"fa-solid fa-trash"}
                    buttonIconPosition={"left"}
                    buttonLabel={"Delete"}
                  />
                </div>
              </div>
            ) : (
              // No Key - Add New
              <div className="text-center py-6">
                <div className="mb-3">
                  <i className="fa-solid fa-key text-gray-400 text-4xl mb-2"></i>
                  <p className="text-sm text-gray-500 font-medium">No API key configured</p>
                  <p className="text-xs text-gray-400 mt-1">Add an API key to enable third-party integrations</p>
                </div>
                <button
                  onClick={() => setIsEditingApiKey(true)}
                  className="bg-teal-600 text-white py-2 px-6 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium shadow-md hover:shadow-lg"
                >
                  <i className="fa-solid fa-plus mr-2"></i>
                  Add API Key
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSettings;